/*!
 * @license
 * Alfresco Example Content Application
 *
 * Copyright (C) 2005 - 2020 Alfresco Software Limited
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail.  Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * along with Alfresco. If not, see <http://www.gnu.org/licenses/>.
 */

import { AppHookService } from '@alfresco/aca-shared';
import { AppStore, SnackbarErrorAction } from '@alfresco/aca-shared/store';
import { SearchQueryBuilderService } from '@alfresco/adf-content-services';
import { AppConfigService } from '@alfresco/adf-core';
import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { ActivatedRoute, NavigationEnd, PRIMARY_OUTLET, Router, RouterEvent, UrlSegment, UrlSegmentGroup, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { SearchInputControlComponent } from 'app/src/app/components/search/search-input-control/search-input-control.component';
import { SearchLibrariesQueryBuilderService } from 'app/src/app/components/search/search-libraries-results/search-libraries-query-builder.service';
import { AldenContentUrlService } from '../../../services/alden-content-url.service';
import { CustomSearchOptionModel, CustomSearchOptionIds } from '../../../models/custom-search-option.model';
import { SearchByTermAction } from '../../../store/actions/search.actions';

@Component({
  selector: 'alden-search-input',
  templateUrl: './search-input.component.html',
  styleUrls: ['./search-input.component.scss'],
  encapsulation: ViewEncapsulation.None,
  host: { class: 'aca-search-input' }
})
export class SearchInputComponent implements OnInit, OnDestroy {
  onDestroy$: Subject<boolean> = new Subject<boolean>();
  hasOneChange = false;
  hasNewChange = false;
  navigationTimer: any;
  has400LibraryError = false;
  searchOnChange: boolean;
 
  searchedWord: string = null;
  searchOptions: Array<CustomSearchOptionModel> = [
    {
      id: CustomSearchOptionIds.Files,
      key: 'SEARCH.INPUT.FILES',
      value: false,
      shouldDisable: this.isLibrariesChecked.bind(this)
    },
    {
      id: CustomSearchOptionIds.Folders,
      key: 'SEARCH.INPUT.FOLDERS',
      value: false,
      shouldDisable: this.isLibrariesChecked.bind(this)
    },
    {
      id: CustomSearchOptionIds.InThisPath,
      key: 'ALDEN.SEARCH.INPUT.IN_THIS_PATH',
      value: false,
      shouldDisable: () => this.isShouldDisableInThisPathChecked()
    }
  ];

  uuidPath: string = null;
  path: string = null;
  currentStartUrl: string = null;
  currentUrl: string = null;
  filterQuery: string = null;

  @ViewChild('searchInputControl', { static: true })
  searchInputControl: SearchInputControlComponent;

  @ViewChild(MatMenuTrigger, { static: true })
  trigger: MatMenuTrigger;

  constructor(
    private queryBuilder: SearchQueryBuilderService,
    private queryLibrariesBuilder: SearchLibrariesQueryBuilderService,
    private config: AppConfigService,
    private router: Router,
    private store: Store<AppStore>,
    private appHookService: AppHookService,
    private contentUrlService: AldenContentUrlService,
    private route: ActivatedRoute
  ) {
    this.searchOnChange = this.config.get<boolean>('search.aca:triggeredOnChange', true);
  }

  ngOnInit() {
    //Escuchar el cambio de path para actualizar el uuid
    this.contentUrlService.path$.subscribe(async _ruta => {  

      const baseUrl =  this.route.snapshot.firstChild?.routeConfig.path.split('/:')[0];

      //Si la ruta anterior no está en la misma base o si te mueves a la base. Ej 'libraries' limpia el checkbox de 'Busqueda aqui'
      if(baseUrl != this.currentStartUrl || baseUrl === this.currentUrl){
        const checkInThisPath = this.searchOptions.find((item) => item.id === CustomSearchOptionIds.InThisPath);
        checkInThisPath.value = false;

        if(!this.isShouldDisableInThisPathChecked())
          this.removeContentFilters();
      }
      
      if(this.isShouldDisableInThisPathChecked() && this.queryBuilder.filterQueries.some((fq) => fq.query.includes('ANCESTOR'))) {
        const checkInThisPath = this.searchOptions.find((item) => item.id === CustomSearchOptionIds.InThisPath);
        checkInThisPath.value = true;
      }

      //Guardar parte inicial de la ruta anterior
      this.currentStartUrl = this.route.snapshot.firstChild?.routeConfig.path.split('/:')[0];
      this.currentUrl = this.router.url;

      this.uuidPath = this.extractUuidFromPath();
      if(!this.onSearchResults && null === this.uuidPath){
        this.path = null;
      }
    });

    this.showInputValue();

    this.router.events
      .pipe(takeUntil(this.onDestroy$))
      .pipe(filter((e) => e instanceof RouterEvent))
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.showInputValue();
        }
      });

    this.appHookService.library400Error.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.has400LibraryError = true;
    });
  }

  showInputValue() {
    this.has400LibraryError = false;
    this.searchedWord = this.getUrlSearchTerm();

    if (this.searchInputControl) {
      this.searchInputControl.searchTerm = this.searchedWord;
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();

    // Importante no poner aqui un removeContentFilters() porque si no, los filterQuery al navegar a SearchResultsComponent se limpian
    //this.removeContentFilters();
  }

  onMenuOpened() {
    this.searchInputControl.searchInput.nativeElement.focus();
  }

  /**
   * Called when the user submits the search, e.g. hits enter or clicks submit
   *
   * @param event Parameters relating to the search
   */
  onSearchSubmit(event: any) {
    const searchTerm = event.target ? (event.target as HTMLInputElement).value : event;
    if (searchTerm) {
      this.searchedWord = searchTerm;

      this.searchByOption();
    } else {
      this.store.dispatch(new SnackbarErrorAction('APP.BROWSE.SEARCH.EMPTY_SEARCH'));    
    }
    this.trigger.closeMenu();
  }

  async onSearchChange(searchTerm: string) {
    if (!this.searchOnChange) {
      return;
    }

    this.has400LibraryError = false;
    this.searchedWord = searchTerm;

    if (this.hasOneChange) {
      this.hasNewChange = true;
    } else {
      this.hasOneChange = true;
    }

    if (this.hasNewChange) {
      clearTimeout(this.navigationTimer);
      this.hasNewChange = false;
    }

    this.navigationTimer = setTimeout(() => {
      if (searchTerm) {
        this.store.dispatch(new SearchByTermAction(searchTerm, this.searchOptions));
      }
      this.hasOneChange = false;
    }, 1000);
  }

   async searchByOption() {
    this.syncInputValues();
    this.has400LibraryError = false;
    
    if (this.isLibrariesChecked()) {
      if (this.onLibrariesSearchResults && this.isSameSearchTerm()) {
        this.queryLibrariesBuilder.update();
      } else if (this.searchedWord) {
        this.store.dispatch(new SearchByTermAction(this.searchedWord, this.searchOptions));
      }
    } else {
      if (this.isFoldersChecked() && !this.isFilesChecked()) {
        await this.filterContent(CustomSearchOptionIds.Folders);
      } else if (this.isFilesChecked() && !this.isFoldersChecked()) {
        await this.filterContent(CustomSearchOptionIds.Files);
      } else {
        if (this.isInThisPathChecked()){
          await this.filterContent(CustomSearchOptionIds.InThisPath);
        } else {
          this.removeContentFilters();
        }
      }

      if (this.onSearchResults && this.isSameSearchTerm()) {
        this.queryBuilder.update();
      } else if (this.searchedWord) {
        this.store.dispatch(new SearchByTermAction(this.searchedWord, this.searchOptions));
      }
    }
  }

  get onLibrariesSearchResults() {
    return this.router.url.indexOf('/search-libraries') === 0;
  }

  get onSearchResults() {
    return !this.onLibrariesSearchResults && this.router.url.indexOf('/search') === 0;
  }

  isInThisPathChecked(): boolean {
    return this.isOptionChecked(CustomSearchOptionIds.InThisPath);
  }

  isFilesChecked(): boolean {
    return this.isOptionChecked(CustomSearchOptionIds.Files);
  }

  isFoldersChecked(): boolean {
    return this.isOptionChecked(CustomSearchOptionIds.Folders);
  }

  isLibrariesChecked(): boolean {
    return this.isOptionChecked(CustomSearchOptionIds.Libraries);
  }

  isOptionChecked(optionId: string): boolean {
    const libItem = this.searchOptions.find((item) => item.id === optionId);
    return !!libItem && libItem.value;
  }

  isContentChecked(): boolean {
    return this.isFilesChecked() || this.isFoldersChecked();
  }

  hasLibraryConstraint(): boolean {
    if (this.isLibrariesChecked()) {
      return this.has400LibraryError || this.searchInputControl.isTermTooShort();
    }
    return false;
  }

  async filterContent(option: CustomSearchOptionIds.Folders | CustomSearchOptionIds.Files | CustomSearchOptionIds.InThisPath) {
    this.removeContentFilters();

    if(option === CustomSearchOptionIds.InThisPath) {
      //Si no estamos en la pantalla de búsqueda
      if(!this.onSearchResults && null !== this.uuidPath){
        this.filterQuery = "+ANCESTOR:'workspace://SpacesStore/"+this.uuidPath+"'";
        this.queryBuilder.addFilterQuery(this.filterQuery);
      }
    } else {
        this.queryBuilder.addFilterQuery(`+TYPE:'cm:${option}'`);
    }
  }

  removeContentFilters() {
    this.queryBuilder.resetToDefaults();
  }

  isShouldDisableInThisPathChecked(): boolean{
    return this.onLibrariesSearchResults || this.onSearchResults;
  }

  private extractUuidFromPath(): string {
    return this.route.snapshot.firstChild?.paramMap.get('folderId');
  }

   syncInputValues() {
    if (this.searchInputControl.searchTerm !== this.searchedWord) {
      if (this.searchInputControl.searchTerm) {
        this.searchedWord = this.searchInputControl.searchTerm;
      } else {
        this.searchInputControl.searchTerm = this.searchedWord;
      }
    }
  }

  getUrlSearchTerm(): string {
    let searchTerm = '';
    if (this.onSearchResults || this.onLibrariesSearchResults) {
      const urlTree: UrlTree = this.router.parseUrl(this.router.url);
      const urlSegmentGroup: UrlSegmentGroup = urlTree.root.children[PRIMARY_OUTLET];

      if (urlSegmentGroup) {
        const urlSegments: UrlSegment[] = urlSegmentGroup.segments;
        searchTerm = urlSegments[0].parameters['q'] ? decodeURIComponent(urlSegments[0].parameters['q']) : '';
      }
    }

    return searchTerm;
  }

  isSameSearchTerm(): boolean {
    const urlSearchTerm = this.getUrlSearchTerm();
    return this.searchedWord === urlSearchTerm;
  }
}